import { useAnalytics } from '../hooks/useAnalytics';
import React from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { Calendar, MapPin, ExternalLink, Clock, Users, Tag, DollarSign, UserCheck, Ticket } from 'lucide-react';
import { colors, spacing, typography, borderRadius, shadows } from '../styles/theme';
import { Event } from '../types/api';
import {
  getVenueName,
  getLocationLabel,
  formatPrice,
  formatCapacity,
  formatAudienceLevel,
  cleanStringArray,
} from '../utils/eventFields';
import {
  compareEventLocalDates,
  formatEventLocalDateTime,
  formatEventLocalDuration,
  localCalendarDateOffset,
} from '../utils/eventDateTime';
import { buildEventPath } from '../utils/slug';
import { displayEntityType } from '../utils/entityTerminology';
import { EventBadges } from './events/EventBadges';
import EventEntityAvatars from './events/EventEntityAvatars';

interface ListEventCardProps {
  event: Event;
  onClick?: (event: Event) => void;
  /** Entity whose page this row is on — collapses its own relationship badge. */
  contextEntityId?: number | null;
}

const ListCard = styled.article`
  background: ${colors.white};
  border: 1px solid ${colors.border.light};
  border-radius: ${borderRadius.md};
  padding: ${spacing[4]};
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    border-color: ${colors.primary[300]};
    box-shadow: ${shadows.sm};
  }

  &:active {
    transform: translateY(0);
  }
`;

const ListCardContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing[3]};
`;

const EventImage = styled.div<{ imageUrl?: string; $isPastEvent?: boolean }>`
  width: 80px;
  height: 60px;
  background: ${props => 
    props.imageUrl 
      ? `url(${props.imageUrl})` 
      : props.$isPastEvent 
        ? `linear-gradient(135deg, ${colors.text.secondary} 0%, ${colors.text.tertiary} 100%)`
        : `linear-gradient(135deg, ${colors.primary[100]} 0%, ${colors.primary[200]} 100%)`
  };
  background-size: cover;
  background-position: center;
  border-radius: ${borderRadius.sm};
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;

  @media (max-width: 768px) {
    width: 60px;
    height: 45px;
  }
`;

const ImagePlaceholder = styled.div`
  color: ${colors.primary[600]};
  font-size: ${typography.fontSizes.xs};
  font-weight: ${typography.fontWeights.medium};
  text-align: center;
`;

const TopSection = styled.div`
  display: flex;
  gap: ${spacing[3]};
  align-items: flex-start;
`;

const EventDetails = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: ${spacing[2]};
`;

const EventTitle = styled.h3`
  font-size: ${typography.fontSizes.base};
  font-weight: ${typography.fontWeights.semibold};
  color: ${colors.primary[600]};
  margin: 0;
  line-height: ${typography.lineHeights.tight};
  cursor: pointer;
  transition: color 0.15s ease;
  
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;

  &:hover {
    color: ${colors.primary[700]};
    text-decoration: underline;
  }
`;

const EventTitleLink = styled.a`
  color: inherit;
  text-decoration: none;

  &:focus-visible {
    outline: 2px solid ${colors.border.focus};
    outline-offset: 2px;
  }
`;

const MetadataGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${spacing[2]};
  min-width: 0;
`;

const MetadataBox = styled.div`
  display: inline-flex;
  align-items: center;
  gap: ${spacing[1]};
  padding: ${spacing[1]} ${spacing[2]};
  background: ${colors.primary[50]};
  border-radius: ${borderRadius.sm};
  font-size: ${typography.fontSizes.xs};
  color: ${colors.text.secondary};
  /* Long unbroken entity names must ellipsize rather than widen the card. */
  min-width: 0;
  max-width: 100%;
  
  svg {
    width: 12px;
    height: 12px;
    color: ${colors.primary[500]};
    flex-shrink: 0;
  }

  span {
    min-width: 0;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
`;

const EventDescription = styled.p`
  font-size: ${typography.fontSizes.sm};
  color: ${colors.text.secondary};
  line-height: ${typography.lineHeights.relaxed};
  margin: 0;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const EventSourcesContainer = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing[2]};
  margin-left: auto;
  flex-shrink: 0;
`;

const SourceIconLink = styled.a`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  background: ${colors.primary[50]};
  border-radius: ${borderRadius.sm};
  color: ${colors.primary[600]};
  transition: all 0.15s ease;
  
  &:hover {
    background: ${colors.primary[100]};
    color: ${colors.primary[700]};
    transform: translateY(-1px);
  }

  svg {
    width: 16px;
    height: 16px;
  }
`;

const ChipRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${spacing[2]};
  margin-top: ${spacing[2]};
`;

const Chip = styled.span<{ $tone?: 'price' | 'audience' | 'topic' | 'neutral' }>`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 2px ${spacing[2]};
  border-radius: ${borderRadius.full};
  font-size: ${typography.fontSizes.xs};
  font-weight: ${typography.fontWeights.medium};
  background: ${(props) =>
    props.$tone === 'price'
      ? colors.success[50]
      : props.$tone === 'audience'
      ? colors.primary[50]
      : colors.background.secondary};
  color: ${(props) =>
    props.$tone === 'price'
      ? colors.success[700]
      : props.$tone === 'audience'
      ? colors.primary[700]
      : colors.text.secondary};
  border: 1px solid ${(props) =>
    props.$tone === 'price'
      ? colors.success[100]
      : props.$tone === 'audience'
      ? colors.primary[100]
      : colors.border.light};

  svg {
    width: 12px;
    height: 12px;
  }
`;

const VenueLine = styled.div`
  display: flex;
  align-items: flex-start;
  gap: ${spacing[1]};
  font-size: ${typography.fontSizes.sm};
  color: ${colors.text.primary};
  
  svg {
    width: 14px;
    height: 14px;
    color: ${colors.primary[500]};
    flex-shrink: 0;
    margin-top: 2px;
  }
`;

const VenueLineText = styled.div`
  display: flex;
  flex-direction: column;
  line-height: ${typography.lineHeights.tight};
  min-width: 0;
`;

const VenueLinePrimary = styled.span`
  font-weight: ${typography.fontWeights.semibold};
`;

const VenueLineSecondary = styled.span`
  font-size: ${typography.fontSizes.xs};
  color: ${colors.text.tertiary};
`;

export const ListEventCard: React.FC<ListEventCardProps> = ({ event, onClick, contextEntityId = null }) => {
  const navigate = useNavigate();
  const { trackEvent } = useAnalytics();

  const isPastEvent = compareEventLocalDates(event.endDate, localCalendarDateOffset(0)) === -1;
  const detailPath = buildEventPath(event);

  const handleClick = (e: React.MouseEvent) => {
    e.preventDefault();
    if (onClick) {
      onClick(event);
    } else if (detailPath) {
      navigate(detailPath);
    }
  };

  // Event details always open in the same tab; only external links leave the app.
  const handleTitleClick = (e: React.MouseEvent) => {
    // Let ctrl/cmd/shift/middle clicks use the anchor's native behaviour.
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
    e.stopPropagation();
    handleClick(e);
  };

  const handleSourceClick = (e: React.MouseEvent, url: string) => {
    e.preventDefault();
    e.stopPropagation();
    trackEvent({ category: 'event_detail', action: 'source_click', custom_parameters: { action_location: 'legacy_event_list', source: 'source_listing' } });
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const eventDate = event.startDate;
  const duration = formatEventLocalDuration(event.startDate, event.endDate);
  const venueName = getVenueName(event.venue);
  const locationLabel = getLocationLabel(event);
  const priceLabel = formatPrice(event.price, event.currency, event.isFree);
  const capacityLabel = formatCapacity(event.capacity, event.ticketsRemaining);
  const audienceLabel = formatAudienceLevel(event.audienceLevel);
  const topics = cleanStringArray(event.topics).slice(0, 3);
  const industries = cleanStringArray(event.industries).slice(0, 2);
  const hasChips = !!(priceLabel || capacityLabel || audienceLabel || topics.length || industries.length);

  return (
    <ListCard onClick={handleClick}>
      <ListCardContent>
        <TopSection>
          <EventImage imageUrl={undefined} $isPastEvent={isPastEvent}>
            <ImagePlaceholder>Event</ImagePlaceholder>
          </EventImage>
          
          <EventDetails>
            <EventTitle>
              <EventTitleLink href={detailPath || undefined} onClick={handleTitleClick}>
                {event.title}
              </EventTitleLink>
            </EventTitle>
            
            <MetadataGrid>
              {eventDate && (
                <MetadataBox>
                  <Calendar />
                  <span>{formatEventLocalDateTime(eventDate, { locale: 'en-US' })}</span>
                </MetadataBox>
              )}
              
              {duration && (
                <MetadataBox>
                  <Clock />
                  <span>{duration}</span>
                </MetadataBox>
              )}
              
              {event.entity && (
                <MetadataBox>
                  <Users />
                  <span>{event.entity.name}</span>
                </MetadataBox>
              )}
              
              {!event.isVirtual && (venueName || locationLabel) && (
                <VenueLine>
                  <MapPin />
                  <VenueLineText>
                    <VenueLinePrimary>{venueName || locationLabel}</VenueLinePrimary>
                    {venueName && locationLabel && (
                      <VenueLineSecondary>{locationLabel}</VenueLineSecondary>
                    )}
                  </VenueLineText>
                </VenueLine>
              )}

              {!event.isVirtual && typeof event.distanceKm === 'number' && (
                <MetadataBox>
                  <MapPin />
                  <span>{event.distanceKm.toFixed(1)} km away</span>
                </MetadataBox>
              )}

              {event.isVirtual && (
                <MetadataBox>
                  <MapPin />
                  <span>Virtual</span>
                </MetadataBox>
              )}
              
              {event.entity?.type && (
                <MetadataBox>
                  <Tag />
                  <span>{displayEntityType(event.entity.type)}</span>
                </MetadataBox>
              )}
            </MetadataGrid>

            <EventEntityAvatars entities={event.entities} />
            <EventBadges event={event} contextEntityId={contextEntityId} />

            {hasChips && (
              <ChipRow>
                {priceLabel && (
                  <Chip $tone="price" title="Price">
                    <DollarSign />
                    {priceLabel}
                  </Chip>
                )}
                {audienceLabel && (
                  <Chip $tone="audience" title="Audience level">
                    <UserCheck />
                    {audienceLabel}
                  </Chip>
                )}
                {capacityLabel && (
                  <Chip title="Capacity">
                    <Ticket />
                    {capacityLabel}
                  </Chip>
                )}
                {topics.map((t) => (
                  <Chip key={`t-${t}`} $tone="topic" title="Topic">
                    {t}
                  </Chip>
                ))}
                {industries.map((i) => (
                  <Chip key={`i-${i}`} $tone="topic" title="Industry">
                    {i}
                  </Chip>
                ))}
              </ChipRow>
            )}
          </EventDetails>

          {event.eventSources && event.eventSources.length > 0 && (
            <EventSourcesContainer>
              {event.eventSources.map((source, index) => (
                <SourceIconLink
                  key={index}
                  href={source}
                  onClick={(e) => handleSourceClick(e, source)}
                  title={`Event Source ${index + 1}`}
                >
                  <ExternalLink />
                </SourceIconLink>
              ))}
            </EventSourcesContainer>
          )}
        </TopSection>

        {event.description && (
          <EventDescription>{event.description}</EventDescription>
        )}
      </ListCardContent>
    </ListCard>
  );
};

export default ListEventCard;